"use client"

import React, { useState, useEffect } from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card"
import { Button } from "../components/ui/button"
import { Label } from "../components/ui/label"
import { Switch } from "../components/ui/switch"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select"
import { Badge } from "../components/ui/badge"
import { Bell, Download, Zap, Crown, LogOut, Settings as SettingsIcon } from "lucide-react"
import { toast } from 'react-hot-toast'
import { useAuth } from "../services/auth"

interface UserPreferences {
  defaultFormat: string;
  autoProcess: boolean;
  notifications: boolean;
}

const SettingsPage: React.FC = () => {
  const { user, logout } = useAuth();

  const [defaultFormat, setDefaultFormat] = useState<string>("mp3")
  const [autoProcess, setAutoProcess] = useState<boolean>(true)
  const [notifications, setNotifications] = useState<boolean>(false)

  const [isEditing, setIsEditing] = useState<boolean>(false)

  useEffect(() => {
    const savedPreferences = localStorage.getItem('userPreferences');
    if (savedPreferences) {
      const parsedPrefs: UserPreferences = JSON.parse(savedPreferences);
      setDefaultFormat(parsedPrefs.defaultFormat);
      setAutoProcess(parsedPrefs.autoProcess);
      setNotifications(parsedPrefs.notifications);
    }
  }, []);

  const handleSaveSettings = async () => {
    try {
      const preferences: UserPreferences = {
        defaultFormat,
        autoProcess,
        notifications
      };

      localStorage.setItem('userPreferences', JSON.stringify(preferences));

      toast.success('Settings updated successfully', {
        style: {
          background: '#4CAF50',
          color: 'white',
          borderRadius: '8px',
        }
      });
      setIsEditing(false);
    } catch (error) {
      toast.error('Failed to save settings', {
        style: {
          background: '#FF6B6B',
          color: 'white',
          borderRadius: '8px',
        }
      });
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      toast.success('Logged out successfully');
    } catch (error) {
      toast.error('Logout failed');
    }
  };

  // if (isLoading) {
  //   return (
  //     <div className="container mx-auto p-6 flex justify-center items-center">
  //       <p>Loading user information...</p>
  //     </div>
  //   );
  // }

  if (!user) {
    return (
      <div className="container mx-auto p-6 flex justify-center items-center">
        <p>Please log in to view settings</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <h1 className="text-3xl font-bold mb-6 flex items-center">
        <SettingsIcon className="mr-3" /> Settings
      </h1>

      <Card>
        <CardHeader>
          <CardTitle>Profile Information</CardTitle>
          <CardDescription>Account details</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="name">Full Name</Label>
            <input 
              id="name"
              type="text"
              value={user.name}
              className="w-full p-2 border rounded"
              disabled
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <input 
              id="email"
              type="email"
              value={user.email}
              className="w-full p-2 border rounded"
              disabled
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Application Settings</CardTitle>
          <CardDescription>Customize your application preferences</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="default-format">Default Output Format</Label>
            <Select value={defaultFormat} onValueChange={setDefaultFormat}>
              <SelectTrigger id="default-format">
                <SelectValue placeholder="Select format" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="mp3">MP3</SelectItem>
                <SelectItem value="wav">WAV</SelectItem>
                <SelectItem value="flac">FLAC</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="auto-process">Auto-process Recordings</Label>
              <p className="text-sm text-muted-foreground">
                Automatically process new recordings
              </p>
            </div>
            <Switch
              id="auto-process"
              checked={autoProcess}
              onCheckedChange={setAutoProcess}
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="notifications">Email Notifications</Label>
              <p className="text-sm text-muted-foreground">
                Receive email updates about your recordings
              </p>
            </div>
            <Switch
              id="notifications"
              checked={notifications}
              onCheckedChange={setNotifications}
            />
          </div>

          <Button onClick={handleSaveSettings} className="w-full">
            Save Application Settings
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Subscription</CardTitle>
          <CardDescription>Manage your subscription and usage</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <h3 className="text-lg font-semibold">Current Plan</h3>
              <p className="text-sm text-muted-foreground">Pro Plan</p>
            </div>
            <Badge variant="secondary" className="text-sm">
              <Crown className="w-4 h-4 mr-1" />
              Pro
            </Badge>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center space-x-2">
              <Download className="w-5 h-5 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium">Storage</p>
                <p className="text-xs text-muted-foreground">50 GB / 100 GB</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Zap className="w-5 h-5 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium">Processing</p>
                <p className="text-xs text-muted-foreground">20 hrs / 50 hrs</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Bell className="w-5 h-5 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium">Next Billing</p>
                <p className="text-xs text-muted-foreground">July 15, 2023</p>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <h3 className="text-lg font-semibold">Upgrade Your Plan</h3>
            <p className="text-sm text-muted-foreground">
              Get more storage, processing time, and advanced features
            </p>
            <Button variant="outline">View Upgrade Options</Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Account Actions</CardTitle>
          <CardDescription>Manage your account</CardDescription>
        </CardHeader>
        <CardContent>
          <Button 
            variant="destructive" 
            className="w-full flex items-center justify-center"
            onClick={handleLogout}
          >
            <LogOut className="mr-2 h-4 w-4" /> Logout
          </Button>
        </CardContent>
      </Card>
    </div>
  )
}

export default SettingsPage